import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import Icon from '@/components/ui/Icon';
import useJobStatus from '@/hooks/api/useJobStatus';
import apiErrorMessage from '../../shared/apiErrors';
import SPAButton from '../../shared/SPAButton';
import SPADialog from '../../shared/SPADialog';
import SPADropdown from '../../shared/SPADropdown';
import { useSPAToast } from '../../shared/SPAToast';
import {
  LessonGuide, SlideShowParent, guideTitle, lessonGuidesKey, useBuildGuidePdf, useCopyLessonGuide,
  useDeleteLessonGuide, usePdfUpdatedPoll, useToggleGuideVisibility,
} from './useLessonGuides';

interface LessonGuideActionsProps {
  guide: LessonGuide;
  parent: SlideShowParent;
  programId: number;
}

type OpenDialog = 'delete' | 'publish' | null;

const ErrorLine = ({ children }: { children: React.ReactNode }) => (
  <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">{children}</p>
);

const Busy = ({ children }: { children: React.ReactNode }) => (
  <span className="tw-text-xs tw-text-spa-tertiary" role="status">{children}</span>
);

// AIDEV-NOTE: The legacy row's links folded into one Actions menu. Present, Preview and Edit are
// full-page links into the slideshow apps (the builder and player are their own apps); everything
// else acts in place. Copy (new builder) and Create PDF are background jobs, so the row shows a
// busy note and polls until they land.
const LessonGuideActions = ({ guide, parent, programId }: LessonGuideActionsProps) => {
  const {
    has_custom_slides: hasCustomSlides,
    id,
    presentation_guide_url: pdfUrl,
    use_new_builder: newBuilder,
    visible,
  } = guide;
  const title = guideTitle(guide);
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const [copyJobId, setCopyJobId] = useState<string | null>(null);
  const [pdfSince, setPdfSince] = useState<number | null>(null);
  const spaToast = useSPAToast();
  const queryClient = useQueryClient();
  const toggleVisibility = useToggleGuideVisibility(programId, parent);
  const destroy = useDeleteLessonGuide(programId, parent);
  const copy = useCopyLessonGuide(programId, parent);
  const buildPdf = useBuildGuidePdf(programId, parent);

  useJobStatus({
    enabled: !!copyJobId,
    jobId: copyJobId,
    onSuccess: (status) => {
      if (status.status === 'completed') {
        setCopyJobId(null);
        queryClient.invalidateQueries({ queryKey: lessonGuidesKey(parent) });
        spaToast.success(`Copied “${title}”.`);
      } else if (status.status === 'failed') {
        setCopyJobId(null);
      }
    },
    refetchInterval: copyJobId ? 2000 : false,
  });

  usePdfUpdatedPoll(parent, id, pdfSince, () => {
    setPdfSince(null);
    spaToast.success(`Built the PDF for “${title}”.`);
  });

  const parentParams = { parent_id: parent.id, parent_type: parent.type, program_id: programId };
  let presentHref: string;
  if (newBuilder) {
    presentHref = Routes.present_mode_shared_slide_show_path(id, { ...parentParams, from: parent.type });
  } else if (parent.type === 'Unit') {
    presentHref = Routes.present_shared_slide_show_path(id, { program_id: programId, unit_id: parent.id });
  } else {
    presentHref = Routes.edit_shared_slide_show_path(id, { lesson_id: parent.id, program_id: programId });
  }
  // The legacy unit page never offered the classic clone.
  const canCopy = newBuilder || parent.type === 'Lesson';

  const confirmPublish = () => toggleVisibility.mutate(guide, {
    onSuccess: () => {
      setOpenDialog(null);
      spaToast.success(`${visible ? 'Unpublished' : 'Published'} “${title}”.`);
    },
  });

  // The row unmounts with the cache patch, taking the dialog with it.
  const confirmDelete = () => destroy.mutate(id, {
    onSuccess: () => spaToast.success(`Deleted “${title}”.`),
  });

  const startCopy = () => copy.mutate(guide, {
    onSuccess: ({ jobId }) => {
      if (jobId) {
        setCopyJobId(jobId);
      } else {
        queryClient.invalidateQueries({ queryKey: lessonGuidesKey(parent) });
        spaToast.success(`Copied “${title}”.`);
      }
    },
  });

  const startPdf = () => buildPdf.mutate(id, { onSuccess: previous => setPdfSince(previous) });

  const handleDialogChange = (dialog: OpenDialog) => (open: boolean) => {
    if (!open) {
      destroy.reset();
      toggleVisibility.reset();
    }
    setOpenDialog(open ? dialog : null);
  };

  let busy: string | null = null;
  if (copyJobId) busy = 'Copying…';
  else if (pdfSince !== null) busy = 'Building PDF…';
  const error = copy.error ?? buildPdf.error;

  return (
    <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
      <SPADropdown>
        <SPADropdown.Trigger asChild>
          <SPAButton aria-label={`Actions for ${title}`} size="xs" variant="outline">
            Actions
            <Icon icon="ChevronDown" size={12} />
          </SPAButton>
        </SPADropdown.Trigger>
        <SPADropdown.Content align="end" className="tw-w-48">
          <SPADropdown.Item asChild>
            <a href={presentHref}>{newBuilder ? 'Present' : 'Preview'}</a>
          </SPADropdown.Item>
          <SPADropdown.Item asChild>
            <a href={Routes.admin_slide_show_path(id, parentParams)}>Edit</a>
          </SPADropdown.Item>
          <SPADropdown.Item onSelect={() => setOpenDialog('publish')}>
            {visible ? 'Unpublish' : 'Publish'}
          </SPADropdown.Item>
          {canCopy && <SPADropdown.Item disabled={!!busy} onSelect={startCopy}>Copy</SPADropdown.Item>}
          {!newBuilder && (
            <SPADropdown.Item disabled={!!busy} onSelect={startPdf}>
              {pdfUrl ? 'Update PDF' : 'Create PDF'}
            </SPADropdown.Item>
          )}
          {pdfUrl && (
            <SPADropdown.Item asChild>
              <a href={pdfUrl} rel="noreferrer" target="_blank">View PDF</a>
            </SPADropdown.Item>
          )}
          <SPADropdown.Separator />
          <SPADropdown.Item
            className="tw-text-spa-error"
            disabled={hasCustomSlides}
            onSelect={() => setOpenDialog('delete')}
          >
            <span className="tw-flex tw-flex-col tw-items-start">
              {hasCustomSlides ? 'Can’t delete' : 'Delete'}
              {hasCustomSlides && (
                <span className="tw-text-xs tw-text-spa-tertiary">A teacher has a copy of it</span>
              )}
            </span>
          </SPADropdown.Item>
        </SPADropdown.Content>
      </SPADropdown>
      {busy && <Busy>{busy}</Busy>}
      {error && <ErrorLine>{apiErrorMessage(error, 'That didn’t start. Try again.')}</ErrorLine>}

      <SPADialog open={openDialog === 'publish'} onOpenChange={handleDialogChange('publish')}>
        <SPADialog.Content size="sm">
          <SPADialog.Header>
            <SPADialog.Title>{visible ? 'Unpublish' : 'Publish'} lesson guide?</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
            <SPADialog.Description>
              {visible
                ? `Unpublishing hides “${title}” from customers.`
                : `Publishing makes “${title}” visible to customers.`}
            </SPADialog.Description>
            {toggleVisibility.isError && (
              <ErrorLine>{apiErrorMessage(toggleVisibility.error, 'The lesson guide could not be updated.')}</ErrorLine>
            )}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={toggleVisibility.isLoading} variant="primary" onClick={confirmPublish}>
              {visible ? 'Unpublish' : 'Publish'}
            </SPAButton>
          </SPADialog.Footer>
        </SPADialog.Content>
      </SPADialog>

      <SPADialog open={openDialog === 'delete'} onOpenChange={handleDialogChange('delete')}>
        <SPADialog.Content size="sm">
          <SPADialog.Header>
            <SPADialog.Title>Delete lesson guide?</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
            <SPADialog.Description>
              This deletes “{title}” with all of its slides, their images, and its game if it has one.
            </SPADialog.Description>
            {destroy.isError && (
              <ErrorLine>{apiErrorMessage(destroy.error, 'The lesson guide could not be deleted.')}</ErrorLine>
            )}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={destroy.isLoading} variant="danger" onClick={confirmDelete}>
              Delete
            </SPAButton>
          </SPADialog.Footer>
        </SPADialog.Content>
      </SPADialog>
    </div>
  );
};

export default LessonGuideActions;
